/**
 * Runtime date normalizer for blog posts.
 *
 * SINGLE SOURCE OF TRUTH: src/lib/canonicalSchedule.ts
 *
 * Every blog post is assigned a datePublished on a uniform 3-day interval
 * starting April 1, 2020 (2020-04-01 -> 2023-03-26). No future dates. No
 * per-post exceptions. The stored `date` field in blogPosts.ts is treated as
 * legacy and ignored for datePublished purposes.
 *
 * dateModified is computed independently in faqGenerator.generateLastUpdated
 * (owner-verified BATCH*_LASTUPDATED values, stored `lastUpdated`, or a
 * deterministic offset) and always falls after the 2020-2023 publish window.
 *
 * See BATCH_PROCESS_GUIDE.md.
 */
import type { BlogPost } from "@/data/blogPosts";
import { PUBLISH_DATE_MAP } from "@/lib/canonicalSchedule";

/**
 * Parses any date string (ISO or human-readable) into a Date object.
 */
function parseDate(dateStr: string): Date {
  const parsed = new Date(dateStr);
  if (!isNaN(parsed.getTime())) {
    return parsed;
  }
  // Fallback: try replacing commas
  const cleaned = dateStr.replace(/,/g, "");
  return new Date(cleaned);
}

/**
 * Normalizes a date to ISO format (YYYY-MM-DD).
 */
function toISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Returns the canonical publish date (YYYY-MM-DD) for every post.
 *
 * Posts in the canonical schedule use their scheduled date verbatim. Any post
 * not present in the schedule (should not happen in normal operation) falls
 * back to its stored `date` normalized to ISO, guarded against future dates.
 *
 * @param posts - Array of blog posts
 * @returns Map of post id -> canonical ISO publish date string
 */
export function normalizePostDates<
  T extends { id: string; slug: string; date: string },
>(posts: T[]): Map<string, string> {
  const dateMap = new Map<string, string>();

  for (const post of posts) {
    const scheduled = PUBLISH_DATE_MAP[post.slug];
    if (scheduled) {
      dateMap.set(post.id, scheduled);
      continue;
    }
    // Fallback for posts not in the canonical schedule: use stored date,
    // clamped so it never falls in the future.
    const stored = toISODate(parseDate(post.date));
    const today = new Date();
    const todayISO = toISODate(today);
    dateMap.set(post.id, stored > todayISO ? todayISO : stored);
  }

  return dateMap;
}

/**
 * Gets the normalized date for a single post.
 * Used by BlogPost.tsx and Blog.tsx to display consistent dates.
 */
export function getNormalizedDate(
  post: { id: string; date: string; slug?: string },
  dateMap: Map<string, string>,
): string {
  return dateMap.get(post.id) || toISODate(parseDate(post.date));
}

/**
 * Formats an ISO date for display (e.g., "April 1, 2020").
 */
export function formatDisplayDate(isoDate: string): string {
  const date = new Date(isoDate + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
